import { reserveUniqueFilename } from './sanitize-filename';

/**
 * A deliverable in a product's shared file pool.
 *
 * `id` is the stable handle a tier's entitlement list (`product.tierFiles`)
 * references; `name` is what the buyer sees and what /api/download keys on.
 * The two are equal for every file the editor writes. `path` is the storage
 * object, and it is the only field that actually locates the bytes.
 */
export type PoolFile = { id: string; name: string; size: number; path: string };

type PoolShape = {
  files?: unknown;
  productFiles?: unknown;
  tierFiles?: unknown;
  pricingModel?: unknown;
};

/**
 * What {@link normalizeFilePool} hands back: the product it was given, plus the
 * two pool fields it guarantees.
 *
 * The function used to be declared `(product: T) => T`, which was simply untrue
 * — it returns `{ ...product, productFiles, tierFiles? }`. Nothing caught it
 * because both call sites pass loosely-typed Firestore data, and the test file
 * that DOES pass object literals was excluded from tsconfig, so the only code
 * positioned to notice was the code TypeScript never looked at.
 *
 * The practical cost of the lie: reading `out.productFiles` off the result was
 * an error on any caller with a precise input type, which is exactly the caller
 * you want to encourage.
 *
 * BOTH fields are optional, and that is the accurate shape rather than a
 * weakening. `normalizeFilePool` returns its input by identity in two cases —
 * already migrated, and no files to migrate — and in the second the input has no
 * pool at all. Promising `productFiles: PoolFile[]` would just move the lie.
 * Every consumer already reads it as
 * `Array.isArray(p.productFiles) ? p.productFiles : []`, so optional is also
 * what the calling code is written against.
 *
 * `tierFiles` is written only for tiered products. That asymmetry is
 * load-bearing and documented on the function: an ABSENT tier record reads as
 * "unlocks everything" to the editor and "unlocks nothing" to /api/download.
 */
export type NormalizedFilePool<T> = T & {
  productFiles?: PoolFile[];
  tierFiles?: { [tierId: string]: string[] };
};

/**
 * Bring a product onto the shared file-pool model in memory, so the product
 * editor only ever has one shape to reason about.
 *
 * Already-migrated products (created by the current create form, or converted
 * by the `backfillTierFilesModel` callable) carry `productFiles` and are
 * returned untouched. Legacy products only have the per-tier `files` map, whose
 * SAME deliverable was uploaded once per tier; those are flattened here.
 *
 * Two invariants make the flatten non-destructive:
 *   - Dedupe is by storage PATH, not by name, and every distinct path is kept.
 *     `onProductUpdated` deletes any path that drops out of `files` on update
 *     (see extractAllFilePaths in functions/src/index.ts), so collapsing two
 *     same-named files from different tiers would destroy a live deliverable.
 *   - When two distinct paths do share a name, the later one is renamed via
 *     reserveUniqueFilename. `name` is only a lookup key — /api/download
 *     resolves the object from `path` — so renaming costs nothing and keeps ids
 *     unique, which the entitlement lists depend on.
 *
 * This is deliberately more forgiving than the server-side backfill, which
 * skips a product outright on a cross-tier filename collision and defers to
 * exactly this path (a re-save through the editor).
 *
 * `tierFiles` is seeded so every tier keeps the files it already had. Note the
 * asymmetry it protects against: an ABSENT tier record means "unlocks
 * everything" to the editor but "unlocks nothing" to /api/download, which is
 * why the editor's save path always writes an explicit list per tier.
 */
export function normalizeFilePool<T extends PoolShape>(product: T): NormalizedFilePool<T> {
  // Both early returns hand back the input unchanged and both need the cast for
  // the same reason: TypeScript cannot carry a narrowing done on a PROPERTY of a
  // generic through to the generic itself. The runtime facts are sound — the
  // falsy guard is for a caller reading straight off a Firestore snapshot, and
  // the second branch has just proved productFiles is a non-empty array.
  if (!product) return product as NormalizedFilePool<T>;
  if (Array.isArray(product.productFiles) && product.productFiles.length > 0) {
    return product as NormalizedFilePool<T>;
  }

  const legacy = (product.files && typeof product.files === 'object')
    ? product.files as Record<string, unknown>
    : {};
  const pool: PoolFile[] = [];
  const pathToId = new Map<string, string>();
  const takenNames = new Set<string>();
  const tierFiles: { [tierId: string]: string[] } = {};

  for (const [tierId, arr] of Object.entries(legacy)) {
    if (!Array.isArray(arr)) continue;
    const ids: string[] = [];
    for (const f of arr as Array<{ name?: string; size?: number; path?: string }>) {
      if (!f?.name || !f?.path) continue;
      let id = pathToId.get(f.path);
      if (!id) {
        id = reserveUniqueFilename(takenNames, f.name);
        pathToId.set(f.path, id);
        pool.push({ id, name: id, size: Number(f.size) || 0, path: f.path });
      }
      if (!ids.includes(id)) ids.push(id);
    }
    // The legacy 'default' bucket was "files every tier gets", not a tier of its
    // own, so it seeds the pool without becoming a tierFiles entry.
    if (tierId !== 'default') tierFiles[tierId] = ids;
  }
  // Nothing legacy to flatten: no `files` map, or one holding no usable rows.
  // Returned by IDENTITY, like the already-migrated branch above — a product
  // with no files needs no pool, and the test suite pins the same object coming
  // back. This is why NormalizedFilePool marks `productFiles` optional.
  if (pool.length === 0) return product as NormalizedFilePool<T>;

  // Anything that came from the 'default' bucket is unlocked by every tier.
  const sharedIds = pool
    .filter(f => !Object.values(tierFiles).some(ids => ids.includes(f.id)))
    .map(f => f.id);
  for (const tierId of Object.keys(tierFiles)) {
    for (const id of sharedIds) {
      if (!tierFiles[tierId].includes(id)) tierFiles[tierId].push(id);
    }
  }

  return {
    ...product,
    productFiles: pool,
    ...(product.pricingModel === 'tiered' ? { tierFiles } : {}),
  };
}

/**
 * Every storage path a pool references. The editor rebuilds the legacy `files`
 * map from the pool on save, and the server deletes whatever paths disappear —
 * so this is the set that must survive a normalize/save round trip untouched
 * for an edit to be non-destructive.
 */
export function poolStoragePaths(pool: readonly PoolFile[]): Set<string> {
  return new Set(pool.map(f => f.path).filter(Boolean));
}

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * A byte count as a buyer reads it, e.g. "1.44 GB".
 *
 * Shared by the download dialog and the product page's spec table so a tier's
 * size reads identically wherever it is quoted. Anything unusable — absent, not
 * a number, negative, infinite — renders as an em-dash rather than "NaN B",
 * because the callers reach this with sizes straight off a Firestore doc, where
 * a legacy row can be missing the field entirely.
 *
 * Units stop at TB: past that the index would run off the end of the table and
 * print "undefined" as the unit.
 */
export function formatFileSize(bytes: number | undefined | null): string {
  if (bytes == null || typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), SIZE_UNITS.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${SIZE_UNITS[i]}`;
}
